/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — Ordre UI (Track J.3a — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Archetype : "ordre" (drag-to-reorder via HTML5 drag/drop + keyboard arrows)
 * ========================================================================== */

import React, { useState, useRef, useCallback } from 'react';
import type { BanqueItemOrdre } from '../../../../lib/banque-types';

export interface OrdreProps {
  item: BanqueItemOrdre;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

export function Ordre({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: OrdreProps): JSX.Element {
  const [order, setOrder] = useState<string[]>(item.itemsOrdre.map((it) => it.id));
  const [submitted, setSubmitted] = useState<boolean>(false);
  const dragIndex = useRef<number | null>(null);

  const itemsById = new Map(item.itemsOrdre.map((it) => [it.id, it]));

  const isCorrect = useCallback((current: string[]): boolean => {
    if (current.length !== item.ordreCorrect.length) return false;
    for (let i = 0; i < current.length; i++) {
      if (current[i] !== item.ordreCorrect[i]) return false;
    }
    return true;
  }, [item]);

  const handleDragStart = (idx: number) => (e: React.DragEvent<HTMLLIElement>): void => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetIdx: number) => (e: React.DragEvent<HTMLLIElement>): void => {
    e.preventDefault();
    const fromIdx = dragIndex.current;
    if (fromIdx === null || fromIdx === targetIdx) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(targetIdx, 0, moved);
      return next;
    });
    dragIndex.current = null;
  };

  const handleDragEnd = (): void => {
    dragIndex.current = null;
  };

  const handleKeyDown = (idx: number) => (e: React.KeyboardEvent<HTMLLIElement>): void => {
    if (submitted || disabled) return;
    if (e.key === 'ArrowUp' && idx > 0) {
      e.preventDefault();
      setOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(idx, 1);
        next.splice(idx - 1, 0, moved);
        return next;
      });
    } else if (e.key === 'ArrowDown' && idx < order.length - 1) {
      e.preventDefault();
      setOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(idx, 1);
        next.splice(idx + 1, 0, moved);
        return next;
      });
    }
  };

  const handleSubmit = (): void => {
    if (submitted || disabled) return;
    setSubmitted(true);
    const ok = isCorrect(order);
    onAnswer(ok, { order });
  };

  const feedback = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : isCorrect(order)
      ? 'correct'
      : 'incorrect'
    : 'idle';

  return (
    <section
      className="ordre"
      data-testid="ordre"
      data-archetype="ordre"
      data-item-id={item.id}
      data-feedback={feedback}
    >
      <h3 className="ordre-consigne">{item.enonce}</h3>
      {item.consigne && <p className="ordre-meta">{item.consigne}</p>}
      <ol
        className="ordre-list"
        data-testid="ordre-list"
        aria-label="Liste à remettre dans le bon ordre (glisser-déposer ou flèches Haut/Bas)"
      >
        {order.map((id, idx) => {
          const it = itemsById.get(id);
          if (!it) return null;
          return (
            <li
              key={id}
              className="ordre-item"
              data-testid={`ordre-item-${id}`}
              data-position={idx + 1}
              draggable={!submitted && !disabled}
              tabIndex={submitted || disabled ? -1 : 0}
              aria-grabbed="false"
              onDragStart={handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(idx)}
              onDragEnd={handleDragEnd}
              onKeyDown={handleKeyDown(idx)}
              aria-label={`${it.label} (position ${idx + 1} sur ${order.length})`}
            >
              <span className="ordre-handle" aria-hidden="true">⋮⋮</span>
              <span className="ordre-label">{it.label}</span>
              <span className="ordre-position" aria-hidden="true">{idx + 1}</span>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="ordre-submit"
        data-action="soumettre"
        data-testid="ordre-submit"
        onClick={handleSubmit}
        disabled={submitted || disabled}
      >
        Valider
      </button>
      {submitted && (
        <p className="ordre-feedback" data-testid="ordre-feedback">
          {isCorrect(order) ? 'Bravo, ordre correct !' : 'Pas tout à fait, réessaie.'}
        </p>
      )}
    </section>
  );
}

export default Ordre;